import z from "zod";

import { proxyAwareServerFetch } from "@/integrations/http/proxy-fetch";

import { hasExactProviderResponseModel } from "./provider-response-model";

export const MAX_JD_IMAGE_BYTES = 1024 * 1024;

export type JdParseImageFile = {
  name: string;
  type?: string;
  data: string;
};

export type ParsedJdTargetInfo = {
  jdText: string;
  company: string | null;
  roleTitle: string | null;
  resumeName: string;
  jdSummary: string;
};

export type JdOcrProvider = {
  extractText(input: { file: JdParseImageFile }): Promise<string>;
};

export type JdTargetInfoProvider = {
  parse(input: { jdText: string }): Promise<ParsedJdTargetInfo>;
};

export type DeepSeekJdTargetInfoConfig = {
  apiKey: string;
  baseURL: string;
  model: string;
  maxTokens: number;
  timeoutMs: number;
  requireExactResponseModel?: boolean;
};

export class JdParseError extends Error {
  constructor(
    message: string,
    readonly code:
      | "INVALID_FILE_TYPE"
      | "FILE_TOO_LARGE"
      | "INVALID_IMAGE_DATA"
      | "JD_TEXT_EMPTY"
      | "OCR_PROVIDER_NOT_CONFIGURED"
      | "OCR_TEXT_EMPTY",
  ) {
    super(message);
    this.name = "JdParseError";
  }
}

export class DeepSeekJdTargetInfoParseError extends Error {
  constructor(
    message: string,
    readonly code: "PROVIDER_ERROR" | "INVALID_PROVIDER_RESPONSE" | "REQUEST_TIMEOUT",
  ) {
    super(message);
    this.name = "DeepSeekJdTargetInfoParseError";
  }
}

const deepSeekToolCallSchema = z.object({
  function: z.object({
    name: z.string(),
    arguments: z.union([z.string(), z.record(z.string(), z.unknown())]),
  }),
});

const deepSeekJdTargetInfoResponseSchema = z.object({
  choices: z.array(
    z.object({
      message: z.object({
        tool_calls: z.array(deepSeekToolCallSchema).optional(),
      }),
    }),
  ),
});

const deepSeekJdTargetInfoArgumentsSchema = z
  .object({
    jd_text: z.string(),
    company: z.string().nullable(),
    role_title: z.string().nullable(),
    resume_name: z.string(),
    jd_summary: z.string(),
  })
  .strict();

function getBase64SizeBytes(base64: string) {
  const normalized = base64.replace(/^data:[^;]+;base64,/, "").replace(/\s/g, "");
  const padding = normalized.endsWith("==") ? 2 : normalized.endsWith("=") ? 1 : 0;

  return Math.max(0, Math.floor((normalized.length * 3) / 4) - padding);
}

function getFileExtension(name: string) {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

export function validateJdOcrImage(file: JdParseImageFile) {
  const extension = getFileExtension(file.name);
  const isImageType = file.type ? file.type.startsWith("image/") : false;
  const isAcceptedImageExtension = ["jpg", "jpeg", "png", "webp"].includes(extension);

  if (file.type === "application/pdf" || extension === "pdf" || (!isImageType && !isAcceptedImageExtension)) {
    throw new JdParseError("JD OCR accepts image files only. PDF files are not supported.", "INVALID_FILE_TYPE");
  }

  if (getBase64SizeBytes(file.data) > MAX_JD_IMAGE_BYTES) {
    throw new JdParseError("JD OCR image must be 1 MB or smaller.", "FILE_TOO_LARGE");
  }
}

function normalizeWhitespace(value: string) {
  return value
    .replace(/\r/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function firstMatch(text: string, patterns: RegExp[]) {
  for (const pattern of patterns) {
    const match = pattern.exec(text);
    const value = match?.[1]?.trim();
    if (value) return value.replace(/[，,。.;；:：]+$/g, "").trim();
  }

  return null;
}

function inferCompany(text: string) {
  return firstMatch(text, [
    /(?:company|employer|organization)\s*[:：]\s*([^\n]+)/i,
    /(?:公司|雇主|企业|單位)\s*[:：]\s*([^\n]+)/,
    /([A-Z][A-Za-z0-9&.,\-\s]{1,80})\s+(?:is hiring|招聘|诚聘|誠聘)/,
  ]);
}

function inferRoleTitle(text: string) {
  return firstMatch(text, [
    /(?:role|title|position|job title)\s*[:：]\s*([^\n]+)/i,
    /(?:岗位|職位|职位|职务|職務|招聘岗位)\s*[:：]\s*([^\n]+)/,
    /(?:hiring|招聘|诚聘|誠聘)\s*[:：]?\s*([^\n]+)/i,
  ]);
}

function buildResumeName(company: string | null, roleTitle: string | null) {
  if (company && roleTitle) return `${company} ${roleTitle}`;
  if (roleTitle) return roleTitle;
  if (company) return `${company} Target`;
  return "Target Resume";
}

function buildJdSummary(text: string) {
  const compact = text.replace(/\s+/g, " ").trim();
  if (compact.length <= 180) return compact;

  const truncated = compact.slice(0, 177).trimEnd();
  const lastSpace = truncated.lastIndexOf(" ");
  return `${lastSpace > 80 ? truncated.slice(0, lastSpace) : truncated}...`;
}

function findTopLevelObjectEnd(text: string) {
  let depth = 0;
  let seenObjectStart = false;
  let inString = false;
  let escaped = false;

  for (let index = 0; index < text.length; index++) {
    const char = text[index];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }

      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (!seenObjectStart) {
      if (char.trim().length === 0) continue;
      if (char !== "{") return null;

      seenObjectStart = true;
      depth = 1;
      continue;
    }

    if (char === "{") {
      depth++;
      continue;
    }

    if (char === "}") {
      depth--;

      if (depth === 0) return index + 1;
      if (depth < 0) return null;
    }
  }

  return null;
}

function parseToolArgumentsJson(argumentsText: string): unknown {
  try {
    return JSON.parse(argumentsText);
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;

    const objectEnd = findTopLevelObjectEnd(argumentsText);
    if (objectEnd === null) throw error;

    const trailingText = argumentsText.slice(objectEnd).trim();
    if (trailingText !== "}") throw error;

    return JSON.parse(argumentsText.slice(0, objectEnd));
  }
}

function buildChatCompletionsUrl(baseURL: string) {
  return `${baseURL.replace(/\/+$/, "")}/chat/completions`;
}

function buildDeepSeekJdParseMessages(jdText: string) {
  return [
    {
      role: "system",
      content: [
        "MultiResume JD target parse prompt phase5.jd_parse.v1.",
        "You parse job description text into editable target-resume setup fields.",
        "You must call the submit_jd_target_info tool exactly once.",
        "Use the provided JD text only. Do not invent company names, role titles, requirements, benefits, locations, or deadlines.",
        "Return null for company or role_title when the text does not explicitly support them.",
        "Keep jd_text as the cleaned original JD text, not a summary.",
        "Keep jd_summary concise and factual.",
        "Map Chinese and English JD text into the same schema.",
      ].join(" "),
    },
    {
      role: "user",
      content: ["Parse this job description:", jdText].join("\n\n"),
    },
  ];
}

function buildSubmitJdTargetInfoTool() {
  return {
    type: "function",
    function: {
      name: "submit_jd_target_info",
      description: "Submit parsed target resume setup fields from a job description.",
      parameters: {
        type: "object",
        additionalProperties: false,
        properties: {
          jd_text: { type: "string" },
          company: { type: ["string", "null"] },
          role_title: { type: ["string", "null"] },
          resume_name: { type: "string" },
          jd_summary: { type: "string" },
        },
        required: ["jd_text", "company", "role_title", "resume_name", "jd_summary"],
      },
      strict: true,
    },
  };
}

function normalizeNullableText(value: string | null) {
  const normalized = value === null ? "" : normalizeWhitespace(value);
  return normalized || null;
}

function getFetchFailureMessage(error: TypeError) {
  const cause = error.cause;

  if (cause && typeof cause === "object" && "code" in cause) {
    return `DeepSeek JD parse request failed due to a network connection error (${String(cause.code)}).`;
  }

  return "DeepSeek JD parse request failed due to a network connection error.";
}

export function parseJdTargetInfoFromText(input: { jdText: string }): ParsedJdTargetInfo {
  const jdText = normalizeWhitespace(input.jdText);
  if (!jdText) throw new JdParseError("JD text is required.", "JD_TEXT_EMPTY");

  const company = inferCompany(jdText);
  const roleTitle = inferRoleTitle(jdText);

  return {
    jdText,
    company,
    roleTitle,
    resumeName: buildResumeName(company, roleTitle),
    jdSummary: buildJdSummary(jdText),
  };
}

export function parseDeepSeekJdTargetInfoResponse(responseBody: unknown): ParsedJdTargetInfo {
  const parsedResponse = deepSeekJdTargetInfoResponseSchema.parse(responseBody);
  const message = parsedResponse.choices[0]?.message;

  if (!message) {
    throw new DeepSeekJdTargetInfoParseError("DeepSeek returned no choices.", "INVALID_PROVIDER_RESPONSE");
  }

  const toolCall = message.tool_calls?.find((call) => call.function.name === "submit_jd_target_info");

  if (!toolCall) {
    throw new DeepSeekJdTargetInfoParseError(
      "DeepSeek did not return the required submit_jd_target_info tool call.",
      "INVALID_PROVIDER_RESPONSE",
    );
  }

  const argumentsText =
    typeof toolCall.function.arguments === "string"
      ? toolCall.function.arguments
      : JSON.stringify(toolCall.function.arguments);
  const parsedArguments = deepSeekJdTargetInfoArgumentsSchema.parse(parseToolArgumentsJson(argumentsText));
  const jdText = normalizeWhitespace(parsedArguments.jd_text);
  if (!jdText) {
    throw new DeepSeekJdTargetInfoParseError("DeepSeek returned empty JD text.", "INVALID_PROVIDER_RESPONSE");
  }

  const company = normalizeNullableText(parsedArguments.company);
  const roleTitle = normalizeNullableText(parsedArguments.role_title);
  const resumeName = normalizeWhitespace(parsedArguments.resume_name) || buildResumeName(company, roleTitle);

  return {
    jdText,
    company,
    roleTitle,
    resumeName,
    jdSummary: normalizeWhitespace(parsedArguments.jd_summary) || buildJdSummary(jdText),
  };
}

export async function parseJdTargetInfoWithDeepSeek(input: {
  jdText: string;
  config: DeepSeekJdTargetInfoConfig;
  fetchImpl?: typeof fetch;
}): Promise<ParsedJdTargetInfo> {
  const jdText = normalizeWhitespace(input.jdText);
  if (!jdText) throw new JdParseError("JD text is required.", "JD_TEXT_EMPTY");

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), input.config.timeoutMs);
  const url = buildChatCompletionsUrl(input.config.baseURL);
  const fetchImpl = input.fetchImpl ?? proxyAwareServerFetch;

  console.info("[MultiResume AI] DeepSeek JD target parse request", {
    baseURL: input.config.baseURL,
    model: input.config.model,
    jdTextChars: jdText.length,
  });

  try {
    const response = await fetchImpl(url, {
      method: "POST",
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${input.config.apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: input.config.model,
        thinking: { type: "disabled" },
        messages: buildDeepSeekJdParseMessages(jdText),
        tools: [buildSubmitJdTargetInfoTool()],
        tool_choice: { type: "function", function: { name: "submit_jd_target_info" } },
        temperature: 0,
        max_tokens: input.config.maxTokens,
      }),
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new DeepSeekJdTargetInfoParseError(
        `DeepSeek JD parse request failed with ${response.status} using model ${input.config.model} at ${url}: ${responseText}`,
        "PROVIDER_ERROR",
      );
    }

    const responseBody = JSON.parse(responseText);
    if (input.config.requireExactResponseModel && !hasExactProviderResponseModel(responseBody, input.config.model)) {
      throw new DeepSeekJdTargetInfoParseError(
        "DeepSeek response model did not match the configured model.",
        "INVALID_PROVIDER_RESPONSE",
      );
    }
    return parseDeepSeekJdTargetInfoResponse(responseBody);
  } catch (error) {
    if (error instanceof DeepSeekJdTargetInfoParseError) throw error;

    if (error instanceof DOMException && error.name === "AbortError") {
      throw new DeepSeekJdTargetInfoParseError("DeepSeek JD parse request timed out.", "REQUEST_TIMEOUT");
    }

    if (error instanceof TypeError && error.message === "fetch failed") {
      throw new DeepSeekJdTargetInfoParseError(getFetchFailureMessage(error), "PROVIDER_ERROR");
    }

    if (error instanceof z.ZodError || error instanceof SyntaxError) {
      throw new DeepSeekJdTargetInfoParseError(
        "DeepSeek JD parse response shape was invalid.",
        "INVALID_PROVIDER_RESPONSE",
      );
    }

    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export async function parseJdTargetInfo(input: {
  jdText?: string | null;
  file?: JdParseImageFile | null;
  ocrProvider?: JdOcrProvider | null;
  targetInfoProvider?: JdTargetInfoProvider | null;
}): Promise<ParsedJdTargetInfo> {
  let jdText = normalizeWhitespace(input.jdText ?? "");

  if (input.file) {
    validateJdOcrImage(input.file);

    if (!input.ocrProvider) {
      throw new JdParseError("JD OCR provider is not configured.", "OCR_PROVIDER_NOT_CONFIGURED");
    }

    const ocrText = normalizeWhitespace(await input.ocrProvider.extractText({ file: input.file }));
    if (!ocrText) throw new JdParseError("No readable JD text was found in the image.", "OCR_TEXT_EMPTY");
    jdText = jdText ? `${jdText}\n\n${ocrText}` : ocrText;
  }

  return input.targetInfoProvider
    ? await input.targetInfoProvider.parse({ jdText })
    : parseJdTargetInfoFromText({ jdText });
}
